import React, {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Comment, ConversationHttpClient } from 'api/conversations';
import { MessageType, Reaction, UserMessage } from 'api/conversations/types';
import { ProjectId } from 'api/models';
import { OnUserMention } from 'components/molecules/mention';
import { CurrentProjectIdContext } from 'contexts';
import {
  ConversationContext,
  createCommentAction,
  deleteCommentAction,
  getAllComments,
  updateCommentAction,
  useConversationStore,
} from 'data/conversation';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { HideIfNotAllowed } from 'hooks/rbac/withAccess';
import {
  ClickEventTags,
  LogType,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { useResetUnreadCount } from 'queries/project-global-conversation/unread-count';
import { queueMacrotask } from 'utils/macrotask';
import { ConversationSkeleton, FetchConversationRow } from './loading';
import { MessagesComment } from './messages-comment';
import { MessagesList } from './messages-list';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ListWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
`;

export const ConversationPane: FC<{
  context: ConversationContext;
  accountId: number;
  projectId: number;
  commentBoxConfig?: {
    className?: string;
    placeholder?: string;
    hidePrivate?: boolean;
  };
  messagesInitialComponent?: ReactNode;
  userMessageContent?: string;
  renderMessageFooterExtra?: (comment: Comment) => ReactNode;
  renderMessageTailComponent?: (comment: Comment) => ReactNode;
  onCommentsLoaded?: (comments: Comment[]) => void;
  messagesComponent?: ReactNode;
  handleUserMention?: OnUserMention;
  apiService: ConversationHttpClient;
  hideAuditLog?: boolean;
  updateLastSeen?: boolean;
  messagesEmptyState?: ReactNode;
}> = ({
  accountId,
  apiService,
  context,
  projectId,
  messagesInitialComponent,
  userMessageContent,
  renderMessageFooterExtra,
  renderMessageTailComponent,
  onCommentsLoaded,
  messagesComponent,
  commentBoxConfig,
  handleUserMention,
  hideAuditLog = false,
  updateLastSeen = true,
  messagesEmptyState,
}) => {
  const [reply, setReply] = useState<UserMessage>();
  const dispatch = useDispatch();
  const {
    comments: allComments = [],
    hasLoadedAllComments,
    loading,
  } = useConversationStore(ProjectId(projectId), context);
  const comments = useMemo(
    () =>
      hideAuditLog
        ? allComments.filter(
            (comment) => comment.messageType === MessageType.UserMessage,
          )
        : allComments,
    [allComments, hideAuditLog],
  );
  const resetUnread = useResetUnreadCount(projectId);
  const isTabVisible = usePageVisibility();
  const { executeLogger } = useLoadLoggerForDataDog(
    [ClickEventTags.CommentPublished],
    LogType.CallbackLog,
  );

  const [scrollToEnd, setScrollToEnd] = useState(false);

  const onAddReactionToComment = (
    commentId: number,
    reactionType: Reaction,
  ): void => {
    apiService.addReaction(projectId, context, commentId, reactionType);
    setScrollToEnd(false);
  };
  const refContext = useRef(context);

  useEffect(() => {
    refContext.current = context;
  }, [context]);
  const lastComment = [...comments]
    .reverse()
    .find((comment) => comment.messageType === MessageType.UserMessage);
  const lastSeenCommentCreatedAt = lastComment?.createdAt;

  useEffect(() => {
    queueMacrotask(() => {
      if (lastSeenCommentCreatedAt && isTabVisible && updateLastSeen) {
        resetUnread(refContext.current, lastSeenCommentCreatedAt);
      }
    });
  }, [isTabVisible, lastSeenCommentCreatedAt, resetUnread, updateLastSeen]);

  useEffect(() => {
    if (comments.length) {
      queueMacrotask(() => {
        onCommentsLoaded?.(comments);
      });
    }
  }, [hasLoadedAllComments, comments, onCommentsLoaded, resetUnread]);

  const clearReply = () => setReply(undefined);

  const addComment = (addedComment: Partial<UserMessage>): void => {
    dispatch(createCommentAction({ comment: addedComment, context }));
    clearReply();
    executeLogger();
    setScrollToEnd(true);
    setScrollToEnd(false);
    if (onCommentsLoaded) {
      queueMacrotask(() => {
        onCommentsLoaded(comments);
      });
    }
  };

  const onPerformReply = (comment: { commentId: number }): void => {
    setScrollToEnd(false);
    const replyMessage = comments.find(
      (conv) => conv.commentId === comment.commentId,
    );

    setReply(replyMessage as UserMessage);
  };

  const onCommentUpdate = ({ commentId, content }: UserMessage): void => {
    setScrollToEnd(false);
    const comment = comments.find((conv) => conv.commentId === commentId);

    dispatch(
      updateCommentAction({
        comment: {
          ...comment,
          content,
        } as UserMessage,
        context,
      }),
    );
  };

  const onPerformDelete = (commentId: number): void => {
    setScrollToEnd(false);
    const toDeleteComment = comments.find((c) => c.commentId === commentId);

    dispatch(
      deleteCommentAction({ comment: toDeleteComment as UserMessage, context }),
    );
  };

  const fetchAllConversation = () => {
    dispatch(getAllComments({ context }));

    setScrollToEnd(false);
  };

  const { hasAccessToResource } = useWithAccess();
  const projectResource = useProjectAsRBACResource(projectId);

  const hasAccessToCreatePrivateComment = hasAccessToResource({
    actionScope: ['PRIVATE'],
    rule: 'COMMENT:CREATE',
    resourceData: projectResource,
  });

  const hasAccessToCreateSharedComment = hasAccessToResource({
    actionScope: ['SHARED'],
    rule: 'COMMENT:CREATE',
    resourceData: projectResource,
  });

  return (
    <CurrentProjectIdContext.Provider value={projectId}>
      <Wrapper>
        <ListWrapper>
          {loading ? (
            <ConversationSkeleton />
          ) : (
            <>
              {!hasLoadedAllComments && (
                <FetchConversationRow
                  fetchAllConversation={fetchAllConversation}
                />
              )}
              <MessagesList
                messagesInitialComponent={messagesInitialComponent}
                renderMessageFooterExtra={renderMessageFooterExtra}
                renderMessageTailComponent={renderMessageTailComponent}
                messages={comments}
                onAddReactionToComment={onAddReactionToComment}
                onCommentUpdate={onCommentUpdate}
                onPerformReply={onPerformReply}
                onPerformDelete={onPerformDelete}
                scrollToEnd={scrollToEnd}
                empty={messagesEmptyState}
              />
            </>
          )}
        </ListWrapper>
        {messagesComponent || (
          <HideIfNotAllowed
            rule={
              context.type === 'PROJECT_CONVERSATIONS'
                ? 'CONVERSATION_COMMENT:CREATE'
                : 'COMMENT:CREATE'
            }
            resourceData={projectResource}
          >
            <MessagesComment
              messageContent={userMessageContent}
              className={commentBoxConfig?.className}
              placeholder={commentBoxConfig?.placeholder}
              hidePrivate={
                commentBoxConfig?.hidePrivate ||
                !hasAccessToCreatePrivateComment
              }
              handleUserMention={handleUserMention}
              reply={reply}
              accountId={accountId}
              projectId={projectId}
              onAddComment={addComment}
              onClearReply={clearReply}
              hasAccessToCreatePrivateCommentsOnly={
                hasAccessToCreatePrivateComment &&
                !hasAccessToCreateSharedComment
              }
            />
          </HideIfNotAllowed>
        )}
      </Wrapper>
    </CurrentProjectIdContext.Provider>
  );
};
